import { identity } from "lodash";
import { FC } from "react";
import { Field, Form } from "react-final-form";

import { Button, Heading, Info, Input, SpacingToken, Text, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { newFeaturesCampaigns } from "constants/campaigns";
import { EMPTY_EDITOR_STATE } from "constants/rich-text";
import { EventPlace, pushDataLayerEvent, trackEngagement } from "helpers/analytics";
import { areShortcutValid } from "helpers/canned-responses-settings";
import { isRichTextMessageEmpty } from "helpers/text-editor";
import { IWithBreakpointsProps, withBreakpoints } from "helpers/withBreakpoints";
import { useNewFeatureForAgents } from "hooks/use-new-feature-for-agents";
import { useAttachments } from "routes/tickets/ticket/message-box/richtext/AttachmentProvider";
import { ITeam } from "store/entities/teams";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "store/entities/transaction/constants";
import { withVar } from "themes/helpers";

import { CannedRichTextEditor } from "./CannedRichTextEditor";
import { ShortcutsInfo } from "./styles";
import { FormValues } from "./types";

interface ICreateCannedResponseProps {
  editorKey: number;
  handleAddCannedResponse: (values: FormValues) => void;
  createCannedResponseTransactionId: string | null;
  disabled?: boolean;
  teams: ITeam[];
  selectedTeamId: string | null;
}

const CreateCannedResponse: FC<ICreateCannedResponseProps & IWithBreakpointsProps> = ({
  editorKey,
  handleAddCannedResponse,
  createCannedResponseTransactionId,
  disabled,
  teams,
  selectedTeamId,
  isDesktop,
}) => {
  const { isUploading, attachmentsSize } = useAttachments();
  const { isNewForAgent, featureFlagValue, setAgentFlagCallback } = useNewFeatureForAgents(
    newFeaturesCampaigns.hasSeenRichTextInCannedResponses.flag,
    newFeaturesCampaigns.hasSeenRichTextInCannedResponses.campaignStartDate
  );

  const shouldDisplayTooltip = isDesktop && isNewForAgent && !featureFlagValue;

  return (
    <Block marginBottom="2em">
      <Heading size="xs" as="h2">
        Canned responses available for: {teams.find(team => team.id === selectedTeamId)?.name || "All teams"}
      </Heading>
      <Text css={{ marginBottom: withVar(SpacingToken.Spacing3) }}>
        Enter the text of the canned response and the shortcuts you want to use to recall it.
      </Text>
      <Form
        key={editorKey}
        onSubmit={handleAddCannedResponse}
        keepDirtyOnReinitialize
        initialValues={
          {
            cannedRichText: EMPTY_EDITOR_STATE,
            cannedShortcuts: "",
          } as FormValues
        }
        render={({ handleSubmit, values, submitting }) => {
          const hasAttachmentOrRichText =
            !isRichTextMessageEmpty(values.cannedRichText) || createCannedResponseTransactionId !== null;
          const disableSubmitButton =
            submitting ||
            !hasAttachmentOrRichText ||
            !areShortcutValid(values.cannedShortcuts) ||
            isUploading() ||
            attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE;

          return (
            <form onSubmit={handleSubmit}>
              <Flex gap="24px" flexWrap>
                <Flex
                  column
                  width="100%"
                  maxWidth="580px"
                  gap={withVar(SpacingToken.Spacing2)}
                  css={{ position: "relative" }}
                >
                  <CannedRichTextEditor
                    editorKey={`create-canned-response-${editorKey}`}
                    richTextAreaMaxHeight="300px"
                    disabled={disabled}
                  />
                  <Tooltip
                    isVisible={shouldDisplayTooltip}
                    placement="right"
                    kind="important"
                    arrowOffsetY={-20}
                    triggerRenderer={() => <Flex />}
                  >
                    <Block width="240px">
                      <Info
                        header="Format your canned response"
                        text="Use advanced text formatting options to properly highlight information and make your message clear."
                        closeWithX
                        handleCloseAction={setAgentFlagCallback}
                      />
                    </Block>
                  </Tooltip>
                  <Block width="100%" maxWidth="480px">
                    <Field
                      name="cannedShortcuts"
                      parse={identity}
                      render={({ input }) => <Input {...input} placeholder="Enter shortcuts..." disabled={disabled} />}
                    />
                    <ShortcutsInfo>
                      To use a canned response while responding to a ticket, enter the # and the shortcut. Use a comma
                      to add more than one shortcut.
                    </ShortcutsInfo>
                  </Block>
                </Flex>
                <Button
                  kind="primary"
                  size="large"
                  type="submit"
                  onClick={() => {
                    pushDataLayerEvent("Canned responses: Add canned response clicked");
                    trackEngagement("Canned responses: Add canned response clicked", EventPlace.Settings);
                  }}
                  disabled={disableSubmitButton}
                  data-testid="create-canned-response-button"
                >
                  Create canned response
                </Button>
              </Flex>
            </form>
          );
        }}
      />
    </Block>
  );
};

export default withBreakpoints(CreateCannedResponse);
